import React from 'react';
import { ScanLine, HelpCircle, X, Camera, Upload } from 'lucide-react';

interface ScannerHeaderProps {
  activeTab: 'camera' | 'upload';
  onTabChange: (tab: 'camera' | 'upload') => void;
  showHelp: boolean;
  onToggleHelp: () => void;
  onClose: () => void;
  cameraActive?: boolean;
}

export const ScannerHeader: React.FC<ScannerHeaderProps> = ({
  activeTab,
  onTabChange,
  showHelp,
  onToggleHelp,
  onClose,
  cameraActive = false,
}) => {
  return (
    <div className="border-b border-slate-200/80 bg-slate-50/90 shrink-0">
      {/* Main Single Row on Desktop (Icon + Title, Tabs, Actions) */}
      <div className="px-3.5 sm:px-4 py-2 sm:py-2.5 flex items-center justify-between gap-2 sm:gap-3">
        {/* Brand / Title */}
        <div className="flex items-center gap-2 min-w-0 shrink-0">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center shadow-2xs shrink-0">
            <ScanLine className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="font-bold text-slate-900 text-xs sm:text-sm leading-tight truncate">
                Pindai Barcode & QR
              </h3>
              {cameraActive && activeTab === 'camera' && (
                <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[9.5px] font-bold border border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Live
                </span>
              )}
            </div>
            <p className="text-[10px] text-slate-500 truncate hidden sm:block">Arahkan kamera ke buku</p>
          </div>
        </div>

        {/* Center Compact Segmented Switcher (Visible on sm+ screens) */}
        <div className="hidden sm:flex bg-slate-200/70 p-0.5 rounded-lg text-xs font-semibold shrink-0">
          <button
            type="button"
            onClick={() => onTabChange('camera')}
            className={`px-3 py-1 rounded-md transition cursor-pointer flex items-center gap-1.5 text-xs ${
              activeTab === 'camera'
                ? 'bg-white text-indigo-700 shadow-2xs font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Camera className="w-3.5 h-3.5" />
            <span>Kamera</span>
          </button>
          <button
            type="button"
            onClick={() => onTabChange('upload')}
            className={`px-3 py-1 rounded-md transition cursor-pointer flex items-center gap-1.5 text-xs ${
              activeTab === 'upload'
                ? 'bg-white text-indigo-700 shadow-2xs font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Upload className="w-3.5 h-3.5" />
            <span>Unggah Berkas</span>
          </button>
        </div>

        {/* Right Action Icons */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onToggleHelp}
            title="Petunjuk Pemindaian"
            className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center transition cursor-pointer ${
              showHelp
                ? 'bg-indigo-100 text-indigo-700 font-bold'
                : 'text-slate-400 hover:text-slate-700 hover:bg-slate-200/60'
            }`}
          >
            <HelpCircle className="w-4 h-4" />
          </button>
          <button
            id="btn-close-scanner"
            type="button"
            onClick={onClose}
            title="Tutup (Esc)"
            className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition cursor-pointer"
            aria-label="Tutup Pemindai"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mobile Sub-Row Segmented Switcher (<sm screens only) */}
      <div className="sm:hidden px-3.5 pb-2 pt-0.5">
        <div className="flex bg-slate-200/70 p-0.5 rounded-lg text-xs font-semibold w-full">
          <button
            type="button"
            onClick={() => onTabChange('camera')}
            className={`flex-1 py-1 rounded-md transition cursor-pointer flex items-center justify-center gap-1.5 text-xs ${
              activeTab === 'camera'
                ? 'bg-white text-indigo-700 shadow-2xs font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Camera className="w-3.5 h-3.5" />
            <span>Kamera</span>
            {cameraActive && (
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse ml-0.5" />
            )}
          </button>
          <button
            type="button"
            onClick={() => onTabChange('upload')}
            className={`flex-1 py-1 rounded-md transition cursor-pointer flex items-center justify-center gap-1.5 text-xs ${
              activeTab === 'upload'
                ? 'bg-white text-indigo-700 shadow-2xs font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Upload className="w-3.5 h-3.5" />
            <span>Unggah Berkas</span>
          </button>
        </div>
      </div>
    </div>
  );
};
